import { zodResolver } from "@hookform/resolvers/zod";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { BriefcaseIcon, FilePdfIcon, ImageIcon, PencilSimpleLineIcon } from "@phosphor-icons/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useBlocker, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { useForm, useFormContext, useWatch } from "react-hook-form";
import { toast } from "sonner";
import z from "zod";

import type { ResumeData } from "@/schema/resume/data";

import { DisclosureNotice } from "@/components/legal/disclosure-notice";
import { Button } from "@/components/ui/button";
import { DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";
import { useConfirm } from "@/hooks/use-confirm";
import { useDialogCloseGuard } from "@/hooks/use-dialog-close-guard";
import { useFormBlocker } from "@/hooks/use-form-blocker";
import { useQuotaPreflight } from "@/hooks/use-quota-preflight";
import { MAX_JD_IMAGE_BYTES } from "@/integrations/multiresume/ai/jd-parse";
import { isCoreV2ProductLine } from "@/integrations/multiresume/core/product-line";
import { getAiActionCost } from "@/integrations/multiresume/quota/costs";
import { client, orpc, type RouterOutput } from "@/integrations/orpc/client";
import { fileToBase64 } from "@/utils/file";
import { deriveImportedResumeName } from "@/utils/resume/import-name";
import { generateId, slugify, stripHtml } from "@/utils/string";
import { cn } from "@/utils/style";
import { getUserFacingError, hasUserFacingErrorCode } from "@/utils/user-facing-error";

import { type DialogProps, useDialogStore } from "../store";
import { navigateToMasterResumeBuilder } from "./master-pdf-navigation";

const nullableTextSchema = z
  .string()
  .trim()
  .transform((value) => (value.length > 0 ? value : null))
  .nullable()
  .optional();

const targetFormSchema = z.object({
  name: z.string().trim().min(1).max(64),
  slug: z.string().min(1).max(64),
  tags: z.array(z.string()),
  company: nullableTextSchema,
  roleTitle: nullableTextSchema,
  jobLink: nullableTextSchema,
  jdText: z.string().trim().max(30_000),
  jdSummary: z.string().trim().max(3000).optional(),
  supplementalInstructions: nullableTextSchema,
});
const targetUpdateFormSchema = targetFormSchema.extend({ jdText: z.string().trim().min(1).max(30_000) });

type TargetFormValues = z.input<typeof targetFormSchema>;
type ParsedTargetFormValues = z.output<typeof targetFormSchema>;
type MasterPdfUpdateMode = "merge" | "replace";
type MasterPdfUpdateStep = "idle" | "reading" | "parsing" | "preparing" | "applying";
type MasterPdfUpdatePreview = RouterOutput["multiResumeWorkspace"]["prepareMasterPdfUpdate"];

function getMasterPdfUpdateErrorCopy(error: unknown, phase: "prepare" | "apply") {
  const code =
    error && typeof error === "object" && "code" in error && typeof error.code === "string" ? error.code : null;

  if (code === "MASTER_UPDATE_STALE") {
    return t`The Complete Resume changed. Refresh the PDF preview before trying again.`;
  }

  return phase === "prepare"
    ? t`The PDF preview could not be prepared. Check the file and try again.`
    : t`The Complete Resume was not confirmed as updated. Keep this preview open and try again.`;
}

const analyzeTargetJdCost = getAiActionCost({
  action: "phase6.jd_analysis.v1",
}).requiredUnits;
const createTargetWithSuggestionsCost = getAiActionCost({
  action: "phase5.first_target_from_raw_master.v1",
}).requiredUnits;
const jdParseOverageCost = getAiActionCost({ action: "phase5.jd_parse.v1" }).requiredUnits;

function buildTargetDefaultValues(input?: Partial<TargetFormValues>): TargetFormValues {
  return {
    name: input?.name ?? "",
    slug: input?.slug ?? "",
    tags: input?.tags ?? [],
    company: input?.company ?? "",
    roleTitle: input?.roleTitle ?? "",
    jobLink: input?.jobLink ?? "",
    jdText: input?.jdText ?? "",
    jdSummary: input?.jdSummary ?? "",
    supplementalInstructions: input?.supplementalInstructions ?? "",
  };
}

function buildTargetSlug(name: string, suffix: string) {
  const prefix = (slugify(name) || "target").slice(0, 64 - suffix.length - 1).replace(/-+$/, "");
  return `${prefix}-${suffix}`;
}

export function CreateTargetResumeDialog({ data }: DialogProps<"resume.target.create">) {
  const coreV2 = isCoreV2ProductLine();
  const navigate = useNavigate();
  const closeDialog = useDialogStore((state) => state.closeDialog);
  const { runWithQuotaPreflight } = useQuotaPreflight();
  const { mutate: createTarget, isPending: isCreatingTarget } = useMutation(
    orpc.multiResumeWorkspace.createTarget.mutationOptions(),
  );
  // createdMasterId: null = never tried, string = master created and reusable
  const [createdMasterId, setCreatedMasterId] = useState<string | null>(null);
  const [isCreatingMaster, setIsCreatingMaster] = useState(false);
  const [isAnalyzingTarget, setIsAnalyzingTarget] = useState(false);
  const [masterPdfFile, setMasterPdfFile] = useState<File | null>(null);

  const form = useForm<TargetFormValues, unknown, ParsedTargetFormValues>({
    resolver: zodResolver(targetFormSchema),
    defaultValues: buildTargetDefaultValues(),
  });

  useSlugFromName(form);
  const jdText = useWatch({ control: form.control, name: "jdText" });
  const isBusy = isCreatingTarget || isCreatingMaster || isAnalyzingTarget;
  const { isDirty, isSubmitting } = form.formState;
  useFormBlocker(form, {
    shouldBlock: () => isDirty && !isSubmitting,
    shouldBlockSilently: () => isBusy,
  });

  const ensureMasterId = async (): Promise<{ masterId: string; masterName: string } | null> => {
    if (data.sourceMasterResumeId && !data.createMasterFirst) {
      return { masterId: data.sourceMasterResumeId, masterName: data.sourceMasterName ?? "Complete Resume" };
    }

    // If master was already created on a previous attempt, reuse it
    if (createdMasterId) {
      return { masterId: createdMasterId, masterName: "My Complete Resume" };
    }

    if (!masterPdfFile) {
      toast.error(t`Upload your Complete Resume PDF before creating a Job Version.`);
      return null;
    }

    // Prevent concurrent Complete Resume creation calls.
    if (isCreatingMaster) return null;

    setIsCreatingMaster(true);
    const toastId = toast.loading(t`Creating complete resume...`);

    try {
      const parsedResumeData = await client.multiResumeAi.parsePdf({
        operationId: generateId(),
        file: {
          name: masterPdfFile.name,
          data: await fileToBase64(masterPdfFile),
        },
      });
      const masterName = deriveImportedResumeName({
        data: parsedResumeData,
        sourceFilename: masterPdfFile.name,
        fallbackName: "My Complete Resume",
      });
      const masterId = await client.multiResumeWorkspace.createMasterFromData({
        data: parsedResumeData,
        sourceFilename: masterPdfFile.name,
        name: masterName,
        slug: slugify(masterName) || generateId(),
        tags: [],
      });
      setCreatedMasterId(masterId);
      toast.success(t`Complete resume created.`, { id: toastId });
      return { masterId, masterName };
    } catch (error) {
      // Do not permanently block retry; let the user try again
      toast.error(getUserFacingError(error, t`Failed to create complete resume.`), { id: toastId });
      return null;
    } finally {
      setIsCreatingMaster(false);
    }
  };

  const submitTarget = async (values: ParsedTargetFormValues, runPrimaryAiAction: boolean) => {
    const masterInfo = await ensureMasterId();
    if (!masterInfo) return;
    const toastId = toast.loading(t`Creating job version...`);
    const analyzeJd = coreV2 && runPrimaryAiAction;
    const generatePlan = !coreV2 && runPrimaryAiAction;
    const operationId = generatePlan ? generateId() : undefined;

    createTarget(
      {
        sourceMasterResumeId: masterInfo.masterId,
        name: values.name,
        slug: values.slug,
        tags: values.tags,
        jdText: values.jdText,
        jdSummary: values.jdSummary?.trim() || undefined,
        company: values.company,
        roleTitle: values.roleTitle,
        jobLink: values.jobLink,
        supplementalInstructions: values.supplementalInstructions,
        generatePlan,
        operationId,
      },
      {
        onSuccess: async ({ id, planGeneration }) => {
          if (analyzeJd) {
            setIsAnalyzingTarget(true);
            try {
              await client.multiResumeWorkspace.analyzeTargetJd({
                targetResumeId: id,
                operationId: generateId(),
              });
              toast.success(t`Job version created. Review the job understanding before generating suggestions.`, {
                id: toastId,
              });
            } catch {
              toast.error(t`Job version created, but job analysis failed. Open job understanding to retry.`, {
                id: toastId,
              });
            } finally {
              setIsAnalyzingTarget(false);
            }
          } else if (generatePlan) {
            toast.success(
              planGeneration === "generated"
                ? t`Job version created with optimization suggestions.`
                : planGeneration === "failed"
                  ? t`Job version created. Optimization suggestions could not be generated yet.`
                  : t`Job version created.`,
              { id: toastId },
            );
          } else {
            toast.success(t`Job version created.`, { id: toastId });
          }

          closeDialog(true);
          void navigate(
            analyzeJd
              ? { to: "/builder/$resumeId", params: { resumeId: id }, hash: "jd-understanding" }
              : { to: "/builder/$resumeId", params: { resumeId: id } },
          );
        },
        onError: (error) => {
          if (hasUserFacingErrorCode(error, "RESUME_SLUG_ALREADY_EXISTS")) {
            toast.error(t`A resume with this slug already exists.`, { id: toastId });
            return;
          }

          // Phase 5 half-failure: master was created but target failed.
          // The masterId is already saved in createdMasterId so retry reuses it.
          if (data.createMasterFirst && masterInfo.masterId) {
            toast.error(
              t`Complete resume was saved, but the job version could not be created. You can retry or create a job version later from your new complete resume on the dashboard.`,
              {
                id: toastId,
                duration: 8000,
              },
            );
          } else {
            toast.error(getUserFacingError(error, t`Failed to create job version.`), { id: toastId });
          }
        },
      },
    );
  };

  const onSubmit = (values: ParsedTargetFormValues, runPrimaryAiAction: boolean) => {
    if (!runPrimaryAiAction) {
      void submitTarget(values, false);
      return;
    }

    void runWithQuotaPreflight(
      coreV2
        ? { action: "phase6.jd_analysis.v1", actionName: t`Analyze job fit` }
        : {
            action: "phase5.first_target_from_raw_master.v1",
            actionName: t`Create and generate target suggestions`,
          },
      () => submitTarget(values, true),
    );
  };

  return (
    <DialogContent
      className="max-h-[min(92vh,860px)] overflow-y-auto sm:max-w-4xl"
      data-testid="create-job-version-dialog"
    >
      <DialogHeader>
        <DialogTitle className="flex items-center gap-x-2">
          <BriefcaseIcon />
          <Trans>Create Job Version</Trans>
        </DialogTitle>
        <DialogDescription>
          {data.createMasterFirst ? (
            <Trans>First we will save your complete resume, then create a JD-bound job version from it.</Trans>
          ) : (
            <Trans>
              Create a job version from {data.sourceMasterName ?? "your complete resume"} and bind it to a JD.
            </Trans>
          )}
        </DialogDescription>
      </DialogHeader>

      <Form {...form}>
        <form className="min-w-0 space-y-5" onSubmit={(event) => event.preventDefault()}>
          {data.createMasterFirst ? (
            <div className="min-w-0 space-y-3 rounded-md border bg-muted/20 p-4">
              <div className="space-y-1">
                <label className="text-sm font-medium" htmlFor="complete-resume-pdf">
                  <Trans>Complete Resume PDF</Trans>
                  <RequiredMark />
                </label>
                <p className="text-xs leading-5 text-muted-foreground">
                  <Trans>
                    Upload a text-based PDF. We will create your editable Complete Resume first, then continue with this
                    Job Version.
                  </Trans>
                </p>
              </div>
              <Input
                id="complete-resume-pdf"
                type="file"
                accept="application/pdf,.pdf"
                onChange={(event) => setMasterPdfFile(event.target.files?.[0] ?? null)}
              />
              <DisclosureNotice surface="pdfUpload" openLinksInNewTab />
            </div>
          ) : null}

          <TargetResumeForm jdRequired={false} />

          <DialogFooter className="min-w-0 sm:block">
            <div className="grid w-full min-w-0 gap-4 lg:grid-cols-[minmax(0,7fr)_minmax(220px,3fr)]">
              <Button
                type="button"
                variant="outline"
                className="w-full min-w-0 whitespace-normal lg:w-fit"
                disabled={isBusy}
                onClick={() => closeDialog()}
              >
                <Trans>Cancel</Trans>
              </Button>

              <div
                className="grid min-w-0 grid-cols-1 gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,2fr)] lg:col-start-2"
                data-testid="target-submit-action-group"
              >
                <Button
                  type="button"
                  variant="secondary"
                  className="h-auto min-h-9 w-full min-w-0 shrink whitespace-normal"
                  disabled={isBusy || (data.createMasterFirst && !masterPdfFile)}
                  onClick={form.handleSubmit((values) => onSubmit(values, false))}
                >
                  {isBusy ? <Spinner /> : null}
                  <Trans>Create Only</Trans>
                </Button>
                <Button
                  type="button"
                  className="h-auto min-h-9 w-full min-w-0 shrink whitespace-normal"
                  data-phase6-entry={!coreV2 ? "proposal-generation-gate" : undefined}
                  disabled={isBusy || !jdText.trim() || (data.createMasterFirst && !masterPdfFile)}
                  onClick={form.handleSubmit((values) => onSubmit(values, true))}
                >
                  {isBusy ? <Spinner /> : null}
                  {coreV2 ? <Trans>Analyze Job Fit</Trans> : <Trans>Create and Generate Suggestions</Trans>}
                  <span aria-hidden="true">◈ {coreV2 ? analyzeTargetJdCost : createTargetWithSuggestionsCost}</span>
                </Button>
              </div>
            </div>
          </DialogFooter>
        </form>
      </Form>
    </DialogContent>
  );
}

export function UpdateTargetInfoDialog({ data }: DialogProps<"resume.target.update">) {
  const queryClient = useQueryClient();
  const closeDialog = useDialogStore((state) => state.closeDialog);
  const { mutateAsync: updateTargetInfo, isPending: isUpdatingTarget } = useMutation(
    orpc.multiResumeWorkspace.updateTargetInfo.mutationOptions(),
  );

  const form = useForm<TargetFormValues, unknown, ParsedTargetFormValues>({
    resolver: zodResolver(targetUpdateFormSchema),
    defaultValues: buildTargetDefaultValues({
      name: data.name,
      slug: data.slug,
      tags: data.tags,
      jdText: data.targetContext.jdText,
      jdSummary: data.targetContext.jdSummary ?? "",
      company: data.targetContext.company ?? "",
      roleTitle: data.targetContext.roleTitle ?? "",
      jobLink: data.targetContext.jobLink ?? "",
      supplementalInstructions: data.targetContext.supplementalInstructions ?? "",
    }),
  });

  useFormBlocker(form);

  const onSubmit = async (values: ParsedTargetFormValues) => {
    const toastId = toast.loading(t`Updating job version info...`);

    try {
      const jdSummary = values.jdSummary?.trim();
      await updateTargetInfo({
        id: data.id,
        name: values.name,
        slug: values.slug,
        tags: values.tags,
        jdText: values.jdText,
        jdSummary: jdSummary || undefined,
        company: values.company,
        roleTitle: values.roleTitle,
        jobLink: values.jobLink,
        supplementalInstructions: values.supplementalInstructions,
      });

      toast.success(t`Job version info updated.`, { id: toastId });
      void queryClient.invalidateQueries({
        queryKey: orpc.multiResumeWorkspace.editorContext.queryKey({ input: { resumeId: data.id } }),
      });
      void queryClient.invalidateQueries({ queryKey: orpc.multiResumeWorkspace.state.queryKey() });
      closeDialog();
    } catch (error) {
      toast.error(getUserFacingError(error, t`Failed to update job version info.`), { id: toastId });
    }
  };

  return (
    <DialogContent className="max-h-[min(92vh,860px)] overflow-y-auto sm:max-w-4xl">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-x-2">
          <PencilSimpleLineIcon />
          <Trans>Job Version Info</Trans>
        </DialogTitle>
        <DialogDescription>
          <Trans>Update resume metadata and JD job version context. This will not rewrite resume content.</Trans>
        </DialogDescription>
      </DialogHeader>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
          <TargetResumeForm jdRequired />

          <DialogFooter>
            <Button type="submit" disabled={isUpdatingTarget}>
              <Trans>Save Changes</Trans>
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </DialogContent>
  );
}

export function UpdateMasterFromPdfDialog({ data }: DialogProps<"resume.master-pdf-update">) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const closeDialog = useDialogStore((state) => state.closeDialog);
  const [file, setFile] = useState<File | null>(null);
  const [mode, setMode] = useState<MasterPdfUpdateMode>("merge");
  const [parsedCache, setParsedCache] = useState<{
    fingerprint: string;
    parsedResumeData: ResumeData;
    sourceFilename: string;
  } | null>(null);
  const [previewCache, setPreviewCache] = useState<Record<string, MasterPdfUpdatePreview>>({});
  const [preview, setPreview] = useState<MasterPdfUpdatePreview | null>(null);
  const [replaceConfirmed, setReplaceConfirmed] = useState(false);
  const [step, setStep] = useState<MasterPdfUpdateStep>("idle");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const authorityEpochRef = useRef(0);
  const prepareAttemptRef = useRef(0);
  const prepareLockRef = useRef(false);
  const prepareToastRef = useRef<ReturnType<typeof toast.loading> | null>(null);
  const applyLockRef = useRef(false);
  const applyCommittedRef = useRef(false);
  const navigationRecoveryAttemptedRef = useRef(false);
  const masterVersionKey = `${data.masterResumeId}:${data.masterUpdatedAt.toISOString()}`;
  const previousMasterVersionKeyRef = useRef(masterVersionKey);
  const { mutateAsync: prepareMasterPdfUpdate, isPending: isPreparing } = useMutation(
    orpc.multiResumeWorkspace.prepareMasterPdfUpdate.mutationOptions(),
  );
  const { mutateAsync: applyMasterPdfUpdate, isPending: isApplying } = useMutation(
    orpc.multiResumeWorkspace.applyMasterPdfUpdate.mutationOptions(),
  );

  const isBusy = isPreparing || isApplying || step !== "idle";
  const confirm = useConfirm();
  const closePromptPendingRef = useRef(false);
  const fileFingerprint = file ? getFileFingerprint(file) : null;
  const previewCacheKey = fileFingerprint
    ? getMasterPdfUpdatePreviewCacheKey({
        mode,
        fileFingerprint,
        masterUpdatedAt: data.masterUpdatedAt,
      })
    : null;
  const cachedPreview = previewCacheKey ? previewCache[previewCacheKey] : undefined;
  const activePreview = preview?.mode === mode ? preview : null;
  const hasUnsavedPdfDraft = Boolean(file || preview || replaceConfirmed);

  const dismissPrepareToast = (toastId = prepareToastRef.current) => {
    if (toastId === null) return;
    if (prepareToastRef.current !== toastId) return;
    toast.dismiss(toastId);
    prepareToastRef.current = null;
  };

  const invalidatePrepareAuthority = () => {
    authorityEpochRef.current += 1;
    prepareAttemptRef.current += 1;
    prepareLockRef.current = false;
    dismissPrepareToast();
    if (!applyLockRef.current) setStep("idle");
  };

  useEffect(() => {
    if (previousMasterVersionKeyRef.current === masterVersionKey) return;

    previousMasterVersionKeyRef.current = masterVersionKey;
    invalidatePrepareAuthority();
    if (!applyLockRef.current) {
      setParsedCache(null);
      setPreviewCache({});
      setPreview(null);
      setReplaceConfirmed(false);
    }
  }, [masterVersionKey]);

  const requestClose = useCallback(async () => {
    if (isBusy || closePromptPendingRef.current) return;
    if (!hasUnsavedPdfDraft) {
      closeDialog();
      return;
    }

    closePromptPendingRef.current = true;
    try {
      if (
        await confirm(t`Leave this Complete Resume PDF update?`, {
          description: t`Your selected PDF and prepared preview will be lost.`,
          confirmText: t`Leave`,
          cancelText: t`Stay`,
        })
      ) {
        (closeDialog as (force?: boolean) => void)(true);
      }
    } finally {
      closePromptPendingRef.current = false;
    }
  }, [closeDialog, confirm, hasUnsavedPdfDraft, isBusy]);

  useBlocker({
    shouldBlockFn: async () => {
      if (applyCommittedRef.current) return false;
      if (isBusy) return true;
      if (!hasUnsavedPdfDraft) return false;
      if (closePromptPendingRef.current) return true;

      closePromptPendingRef.current = true;
      try {
        return !(await confirm(t`Leave this Complete Resume PDF update?`, {
          description: t`Your selected PDF and prepared preview will be lost.`,
          confirmText: t`Leave`,
          cancelText: t`Stay`,
        }));
      } finally {
        closePromptPendingRef.current = false;
      }
    },
    enableBeforeUnload: () => !applyCommittedRef.current && (isBusy || hasUnsavedPdfDraft),
  });

  useDialogCloseGuard(() => {
    if (applyCommittedRef.current) return true;
    if (isBusy) return false;
    return hasUnsavedPdfDraft ? "confirm" : true;
  });

  const onFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextFile = event.target.files?.[0] ?? null;
    if (applyLockRef.current) return;
    if ((nextFile ? getFileFingerprint(nextFile) : null) === fileFingerprint) return;

    invalidatePrepareAuthority();
    setFile(nextFile);
    setParsedCache(null);
    setPreviewCache({});
    setPreview(null);
    setReplaceConfirmed(false);
  };

  const onSelectMode = (nextMode: MasterPdfUpdateMode) => {
    if (applyLockRef.current || nextMode === mode) return;
    invalidatePrepareAuthority();
    setMode(nextMode);
    setReplaceConfirmed(false);

    if (!fileFingerprint) {
      setPreview(null);
      return;
    }

    const nextCacheKey = getMasterPdfUpdatePreviewCacheKey({
      mode: nextMode,
      fileFingerprint,
      masterUpdatedAt: data.masterUpdatedAt,
    });
    setPreview(previewCache[nextCacheKey] ?? null);
  };

  const onReplaceConfirmationChange = (confirmed: boolean) => {
    if (applyLockRef.current) return;
    setReplaceConfirmed(confirmed);
  };

  const ensureParsedResumeData = async (requestEpoch: number) => {
    if (!file) {
      toast.error(t`Choose a PDF file first.`);
      return null;
    }

    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
      toast.error(t`Only PDF files are supported.`);
      return null;
    }

    const fingerprint = getFileFingerprint(file);
    if (parsedCache?.fingerprint === fingerprint) return parsedCache;

    setStep("reading");
    const base64Data = await fileToBase64(file);
    if (requestEpoch !== authorityEpochRef.current) return null;

    setStep("parsing");
    const parsedResumeData = await client.multiResumeAi.parsePdf({
      operationId: generateId(),
      file: { name: file.name, data: base64Data },
    });
    if (requestEpoch !== authorityEpochRef.current) return null;

    const nextCache = { fingerprint, parsedResumeData, sourceFilename: file.name };
    setParsedCache(nextCache);
    return nextCache;
  };

  const onPrepare = async (nextMode: MasterPdfUpdateMode, options: { force?: boolean } = {}) => {
    if (prepareLockRef.current || applyLockRef.current) return;

    if (fileFingerprint && !options.force) {
      const cacheKey = getMasterPdfUpdatePreviewCacheKey({
        mode: nextMode,
        fileFingerprint,
        masterUpdatedAt: data.masterUpdatedAt,
      });
      const existingPreview = previewCache[cacheKey];
      if (existingPreview) {
        setPreview(existingPreview);
        return;
      }
    }

    prepareLockRef.current = true;
    const prepareAttempt = prepareAttemptRef.current + 1;
    prepareAttemptRef.current = prepareAttempt;
    const requestEpoch = authorityEpochRef.current;
    const toastId = toast.loading(
      nextMode === "merge"
        ? t`Preparing complete resume merge preview...`
        : t`Preparing complete resume replace preview...`,
    );
    prepareToastRef.current = toastId;
    setStep("reading");

    const isCurrentAttempt = () =>
      prepareAttemptRef.current === prepareAttempt && requestEpoch === authorityEpochRef.current;
    const finishAttempt = () => {
      if (prepareAttemptRef.current !== prepareAttempt) return;
      prepareLockRef.current = false;
      if (!applyLockRef.current) setStep("idle");
      if (prepareToastRef.current === toastId) prepareToastRef.current = null;
    };

    try {
      const parsed = await ensureParsedResumeData(requestEpoch);
      if (!parsed) {
        dismissPrepareToast(toastId);
        return;
      }

      if (!isCurrentAttempt()) {
        dismissPrepareToast(toastId);
        return;
      }
      setStep("preparing");
      const nextPreview = await prepareMasterPdfUpdate({
        masterResumeId: data.masterResumeId,
        mode: nextMode,
        parsedResumeData: parsed.parsedResumeData,
        expectedMasterUpdatedAt: data.masterUpdatedAt,
        sourceFilename: parsed.sourceFilename,
        operationId: nextMode === "merge" ? generateId() : undefined,
      });
      if (!isCurrentAttempt()) {
        dismissPrepareToast(toastId);
        return;
      }

      const cacheKey = getMasterPdfUpdatePreviewCacheKey({
        mode: nextMode,
        fileFingerprint: parsed.fingerprint,
        masterUpdatedAt: nextPreview.masterUpdatedAt,
      });

      setPreview(nextPreview);
      setPreviewCache((cache) => ({ ...cache, [cacheKey]: nextPreview }));
      toast.success(nextMode === "merge" ? t`Merge preview is ready.` : t`Replace preview is ready.`, { id: toastId });
      if (prepareToastRef.current === toastId) prepareToastRef.current = null;
    } catch (error) {
      if (isCurrentAttempt()) {
        toast.error(getMasterPdfUpdateErrorCopy(error, "prepare"), { id: toastId });
        if (prepareToastRef.current === toastId) prepareToastRef.current = null;
      } else {
        dismissPrepareToast(toastId);
      }
    } finally {
      finishAttempt();
    }
  };

  const onApply = async () => {
    if (!activePreview || prepareLockRef.current || applyLockRef.current) return;

    applyLockRef.current = true;
    const applyPayload = {
      masterResumeId: data.masterResumeId,
      mode: activePreview.mode,
      draftData: activePreview.draftData,
      expectedMasterUpdatedAt: activePreview.masterUpdatedAt,
      summary: activePreview.summary,
      warnings: activePreview.warnings,
    };
    setStep("applying");
    const toastId = toast.loading(t`Applying complete resume update...`);

    try {
      await applyMasterPdfUpdate(applyPayload);

      toast.success(t`Complete resume updated. Job versions will show that the complete resume has changed.`, {
        id: toastId,
      });
      void queryClient.invalidateQueries({ queryKey: orpc.multiResumeWorkspace.state.queryKey() });
      void queryClient.invalidateQueries({
        queryKey: orpc.multiResumeWorkspace.editorContext.queryKey({
          input: { resumeId: applyPayload.masterResumeId },
        }),
      });
      // The server mutation is committed. Clear only the obsolete local draft
      // guards before starting the one permitted transition; the mutation must
      // never be replayed as part of route recovery.
      applyCommittedRef.current = true;
      closeDialog(true);
      await navigateToMasterResumeBuilder({
        masterResumeId: applyPayload.masterResumeId,
        navigate,
        recover: (path) => {
          if (navigationRecoveryAttemptedRef.current || window.location.pathname === path) return;
          navigationRecoveryAttemptedRef.current = true;
          window.location.replace(path);
        },
      });
    } catch (error) {
      toast.error(getMasterPdfUpdateErrorCopy(error, "apply"), { id: toastId });
    } finally {
      applyLockRef.current = false;
      setStep("idle");
    }
  };

  return (
    <DialogContent className="max-h-[min(92vh,860px)] overflow-y-auto sm:max-w-3xl">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-x-2">
          <FilePdfIcon />
          <Trans>Update Complete Resume from PDF</Trans>
        </DialogTitle>
        <DialogDescription>
          <Trans>
            {data.masterName} is already your active complete resume. Parse a text-based PDF, preview a merge or
            replacement, then apply only after confirmation.
          </Trans>
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-5">
        <DisclosureNotice surface="pdfUpload" openLinksInNewTab />

        <div className="space-y-2">
          <label className="text-sm font-medium" htmlFor="master-pdf-update-file">
            <Trans>PDF file</Trans>
          </label>
          <Input
            ref={fileInputRef}
            id="master-pdf-update-file"
            type="file"
            accept="application/pdf,.pdf"
            className="sr-only !h-px !w-px"
            disabled={isBusy}
            onChange={onFileChange}
          />
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <Button type="button" variant="outline" disabled={isBusy} onClick={() => fileInputRef.current?.click()}>
              <Trans>Choose a PDF file</Trans>
            </Button>
            <span className="min-w-0 truncate text-sm text-muted-foreground">
              {file?.name ?? <Trans>No PDF selected</Trans>}
            </span>
          </div>
          <p className="text-xs text-muted-foreground">
            <Trans>
              Scanned PDFs are not supported yet. The parsed PDF and prepared preview are cached while this dialog is
              open.
            </Trans>
          </p>
        </div>

        <div className="inline-grid rounded-md border bg-muted/20 p-1 sm:grid-cols-2">
          <Button
            type="button"
            variant={mode === "merge" ? "secondary" : "ghost"}
            className={cn("justify-center", mode !== "merge" && "text-muted-foreground")}
            disabled={isBusy}
            onClick={() => onSelectMode("merge")}
          >
            <Trans>Merge into Complete Resume</Trans>
          </Button>
          <Button
            type="button"
            variant={mode === "replace" ? "secondary" : "ghost"}
            className={cn("justify-center", mode !== "replace" && "text-muted-foreground")}
            disabled={isBusy}
            onClick={() => onSelectMode("replace")}
          >
            <Trans>Replace Complete Resume</Trans>
          </Button>
        </div>

        {step !== "idle" ? (
          <div
            className="flex items-center gap-2 rounded-md border bg-muted/20 p-3 text-sm"
            role="status"
            aria-live="polite"
          >
            <Spinner />
            {step === "reading" ? <Trans>Reading PDF...</Trans> : null}
            {step === "parsing" ? <Trans>Parsing PDF...</Trans> : null}
            {step === "preparing" ? <Trans>Preparing preview...</Trans> : null}
            {step === "applying" ? <Trans>Applying update...</Trans> : null}
          </div>
        ) : null}

        <div
          className={cn(
            "space-y-4 rounded-md border bg-muted/20 p-4",
            mode === "replace" && "border-amber-500/30 bg-amber-500/10",
          )}
        >
          <div className="space-y-1">
            <h3 className="text-sm font-medium">
              {mode === "merge" ? <Trans>Merge Preview</Trans> : <Trans>Replace Preview</Trans>}
            </h3>
            <p className="text-sm text-muted-foreground">
              {mode === "merge" ? (
                <Trans>
                  Compare the current Complete Resume with the parsed PDF and inspect the merged draft before applying
                  it.
                </Trans>
              ) : (
                <Trans>
                  Inspect the parsed identity, summary, section counts, and warnings before replacing anything.
                </Trans>
              )}
            </p>
          </div>

          {activePreview ? <MasterPdfUpdatePreviewPane preview={activePreview} sourceFilename={file?.name} /> : null}

          <Button
            type="button"
            variant="secondary"
            disabled={!file || isBusy}
            onClick={() => void onPrepare(mode, { force: !!cachedPreview })}
          >
            {isPreparing ? <Spinner /> : null}
            {mode === "merge" ? (
              cachedPreview ? (
                <Trans>Regenerate Merge Preview</Trans>
              ) : (
                <Trans>Prepare Merge Preview</Trans>
              )
            ) : cachedPreview ? (
              <Trans>Revalidate Replacement</Trans>
            ) : (
              <Trans>Prepare Replacement</Trans>
            )}
          </Button>
        </div>

        {mode === "replace" ? (
          <label className="flex gap-3 rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-sm">
            <input
              type="checkbox"
              className="mt-1 size-4 shrink-0"
              checked={replaceConfirmed}
              disabled={isBusy}
              onChange={(event) => onReplaceConfirmationChange(event.target.checked)}
            />
            <span>
              <Trans>
                I understand this will replace the current Complete Resume content. A rollback snapshot will be stored
                before applying.
              </Trans>
            </span>
          </label>
        ) : null}
      </div>

      <DialogFooter>
        <Button variant="outline" disabled={isBusy} onClick={() => void requestClose()}>
          <Trans>Cancel</Trans>
        </Button>
        <Button
          type="button"
          disabled={!activePreview || isBusy || (mode === "replace" && !replaceConfirmed)}
          onClick={onApply}
        >
          {isApplying ? <Spinner /> : null}
          {mode === "merge" ? <Trans>Apply Merge Result</Trans> : <Trans>Confirm Replacement</Trans>}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

function MasterPdfUpdatePreviewPane({
  preview,
  sourceFilename,
}: {
  preview: MasterPdfUpdatePreview;
  sourceFilename?: string;
}) {
  const stats = getResumeDataPreviewStats(preview.draftData);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-medium">
          <Trans>Prepared Draft</Trans>
        </h3>
        <span className="rounded-full border px-2 py-1 text-xs text-muted-foreground">
          {preview.mode === "merge" ? <Trans>Merge draft</Trans> : <Trans>Replacement draft</Trans>}
        </span>
      </div>

      <div className="grid gap-3 text-sm sm:grid-cols-4">
        <PreviewStat label={<Trans>File</Trans>} value={sourceFilename || "-"} />
        <PreviewStat label={<Trans>Identity</Trans>} value={stats.name || "-"} />
        <PreviewStat label={<Trans>Experience</Trans>} value={String(stats.experienceCount)} />
        <PreviewStat label={<Trans>Projects</Trans>} value={String(stats.projectCount)} />
      </div>

      {stats.summary ? (
        <div className="space-y-1">
          <p className="text-xs font-medium text-muted-foreground uppercase">
            <Trans>Summary preview</Trans>
          </p>
          <p className="line-clamp-3 text-sm text-muted-foreground">{stats.summary}</p>
        </div>
      ) : null}

      {preview.sectionStats.length > 0 ? (
        <div className="overflow-hidden rounded-md border" data-testid="master-pdf-section-counts">
          <div
            className="grid grid-cols-[minmax(6rem,1fr)_minmax(2.75rem,5rem)_minmax(2.75rem,5rem)_minmax(2.75rem,5rem)] gap-2 border-b bg-muted/40 px-3 py-2 text-xs font-medium text-muted-foreground sm:grid-cols-[minmax(0,1fr)_5rem_5rem_5rem]"
            data-testid="master-pdf-section-counts-header"
          >
            <div className="min-w-0 break-words">
              <Trans>Section</Trans>
            </div>
            <div className="min-w-0 text-end">
              <Trans>Before</Trans>
            </div>
            <div className="min-w-0 text-end">
              <Trans>After</Trans>
            </div>
            <div className="min-w-0 text-end">
              <Trans>Added</Trans>
            </div>
          </div>
          {preview.sectionStats.map((section) => (
            <div
              key={section.sectionId}
              className="grid grid-cols-[minmax(6rem,1fr)_minmax(2.75rem,5rem)_minmax(2.75rem,5rem)_minmax(2.75rem,5rem)] gap-2 border-b px-3 py-2 text-sm last:border-b-0 sm:grid-cols-[minmax(0,1fr)_5rem_5rem_5rem]"
            >
              <div className="min-w-0 break-words">{getMasterPdfUpdateSectionLabel(section.sectionId)}</div>
              <div className="min-w-0 text-end tabular-nums">{section.originalCount}</div>
              <div className="min-w-0 text-end tabular-nums">
                {getResumeDataSectionCount(preview.draftData, section.sectionId)}
              </div>
              <div className="min-w-0 text-end tabular-nums">{section.addedCount}</div>
            </div>
          ))}
        </div>
      ) : null}

      {preview.summary.length > 0 ? (
        <div className="space-y-2">
          <p className="text-sm font-medium">
            <Trans>What will change</Trans>
          </p>
          <ul className="list-disc space-y-1 ps-5 text-sm text-muted-foreground">
            {preview.summary.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {preview.warnings.length > 0 ? (
        <div className="space-y-2 rounded-md border border-amber-500/30 bg-amber-500/10 p-3">
          <p className="text-sm font-medium">
            <Trans>Review before applying</Trans>
          </p>
          <ul className="list-disc space-y-1 ps-5 text-sm text-muted-foreground">
            {preview.warnings.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function PreviewStat({ label, value }: { label: React.ReactNode; value: string }) {
  return (
    <div className="min-w-0 rounded-md border bg-background p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 min-w-0 truncate font-medium">{value}</p>
    </div>
  );
}

function getFileFingerprint(file: File) {
  return `${file.name}:${file.size}:${file.lastModified}`;
}

function getMasterPdfUpdatePreviewCacheKey(input: {
  mode: MasterPdfUpdateMode;
  fileFingerprint: string;
  masterUpdatedAt: Date;
}) {
  return `${input.masterUpdatedAt.toISOString()}:${input.fileFingerprint}:${input.mode}`;
}

function getMasterPdfUpdateSectionLabel(sectionId: MasterPdfUpdatePreview["sectionStats"][number]["sectionId"]) {
  if (sectionId === "education") return t`Education`;
  if (sectionId === "experience") return t`Experience`;
  if (sectionId === "projects") return t`Projects`;
  if (sectionId === "skills") return t`Skills`;
  if (sectionId === "certifications") return t`Certifications`;
  return t`Awards`;
}

function getResumeDataPreviewStats(data: ResumeData) {
  const summary = stripHtml(data.summary.content).replace(/\s+/g, " ").trim();

  return {
    name: data.basics.name.trim(),
    summary,
    experienceCount: data.sections.experience.items.filter((item) => !item.hidden).length,
    projectCount: data.sections.projects.items.filter((item) => !item.hidden).length,
  };
}

function getResumeDataSectionCount(
  data: ResumeData,
  sectionId: MasterPdfUpdatePreview["sectionStats"][number]["sectionId"],
) {
  return data.sections[sectionId].items.filter((item) => !item.hidden).length;
}

function useSlugFromName(form: ReturnType<typeof useForm<TargetFormValues, unknown, ParsedTargetFormValues>>) {
  const name = useWatch({ control: form.control, name: "name" });
  const suffix = useRef<string | null>(null);

  useEffect(() => {
    if (!name) return;
    // Keep one 50-bit random suffix per creation draft, including failed-submit retries.
    suffix.current ??= Array.from(
      crypto.getRandomValues(new Uint8Array(10)),
      (byte) => "abcdefghijklmnopqrstuvwxyz234567"[byte & 31],
    ).join("");
    form.setValue("slug", buildTargetSlug(name, suffix.current), { shouldDirty: true, shouldValidate: true });
  }, [form, name]);
}

function RequiredMark() {
  return (
    <span aria-hidden="true" className="text-destructive">
      *
    </span>
  );
}

function TargetResumeForm({ jdRequired }: { jdRequired: boolean }) {
  const form = useFormContext<TargetFormValues>();
  const { runWithQuotaPreflight } = useQuotaPreflight();
  const [jdImageFile, setJdImageFile] = useState<File | null>(null);
  const [isDraggingJdImage, setIsDraggingJdImage] = useState(false);
  const { data: jdParsePreflight } = useQuery(
    orpc.multiResumeQuota.preflight.queryOptions({
      input: { action: "phase5.jd_parse.v1" },
    }),
  );
  const { mutateAsync: parseJdTargetInfo, isPending: isParsingJd } = useMutation({
    mutationFn: async (input: {
      operationId: string;
      jdText?: string | null;
      file?: { name: string; type?: string; data: string } | null;
    }) => await client.multiResumeAi.parseJdTargetInfo(input),
  });
  const name = useWatch({ control: form.control, name: "name" });
  const company = useWatch({ control: form.control, name: "company" });
  const roleTitle = useWatch({ control: form.control, name: "roleTitle" });

  const setJdImageFromFile = (file: File | null) => {
    if (!file) {
      setJdImageFile(null);
      return true;
    }

    const extension = file.name.split(".").pop()?.toLowerCase();
    const isImage = file.type.startsWith("image/") || ["jpg", "jpeg", "png", "webp"].includes(extension ?? "");

    if (!isImage || file.type === "application/pdf" || extension === "pdf") {
      toast.error(t`JD OCR accepts image files only. PDF files are not supported.`);
      setJdImageFile(null);
      return false;
    }

    if (file.size > MAX_JD_IMAGE_BYTES) {
      toast.error(t`JD OCR image must be 1 MB or smaller.`);
      setJdImageFile(null);
      return false;
    }

    setJdImageFile(file);
    return true;
  };

  useEffect(() => {
    if (name?.trim()) return;

    const nextName = [company, roleTitle]
      .map((value) => value?.trim())
      .filter((value): value is string => Boolean(value))
      .join(" - ");
    if (!nextName) return;

    form.setValue("name", nextName, { shouldDirty: true, shouldValidate: true });
  }, [company, form, name, roleTitle]);

  const onJdImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!setJdImageFromFile(event.target.files?.[0] ?? null)) {
      event.target.value = "";
    }
  };

  const onJdImageDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDraggingJdImage(false);
    setJdImageFromFile(event.dataTransfer.files?.[0] ?? null);
  };

  const parseJd = async () => {
    const toastId = toast.loading(t`Parsing JD...`);

    try {
      const file = jdImageFile
        ? {
            name: jdImageFile.name,
            type: jdImageFile.type,
            data: await fileToBase64(jdImageFile),
          }
        : null;
      const result = await parseJdTargetInfo({
        operationId: generateId(),
        jdText: form.getValues("jdText"),
        file,
      });

      form.setValue("jdText", result.jdText, { shouldDirty: true, shouldValidate: true });
      form.setValue("jdSummary", result.jdSummary ?? "", { shouldDirty: true, shouldValidate: true });
      form.setValue("company", result.company ?? "", { shouldDirty: true, shouldValidate: true });
      form.setValue("roleTitle", result.roleTitle ?? "", { shouldDirty: true, shouldValidate: true });

      if (!form.getValues("name")) {
        form.setValue("name", result.resumeName, { shouldDirty: true, shouldValidate: true });
      }

      toast.success(t`JD parsed. Review the fields before creating the job version.`, { id: toastId });
    } catch (error) {
      toast.error(getUserFacingError(error, t`Failed to parse JD.`), { id: toastId });
    }
  };

  const onParseJd = async () => {
    await runWithQuotaPreflight(
      {
        action: "phase5.jd_parse.v1",
        actionName: t`Parse JD`,
      },
      parseJd,
    );
  };

  const jdParseCostIndicator =
    jdParsePreflight?.chargedUnits === 0 && jdParsePreflight.jdParseAllowance ? (
      <span className="text-xs" aria-live="polite">
        <Trans>
          Free parses today {jdParsePreflight.jdParseAllowance.dailyUsed} /{" "}
          {jdParsePreflight.jdParseAllowance.dailyLimit}
        </Trans>
      </span>
    ) : (
      <span aria-hidden="true">◈ {jdParseOverageCost}</span>
    );

  return (
    <>
      <div
        className="grid min-w-0 items-start gap-3 lg:grid-cols-[minmax(0,7fr)_minmax(220px,3fr)] lg:gap-x-4"
        data-testid="jd-input-section"
      >
        <FormField
          control={form.control}
          name="jdText"
          render={({ field }) => (
            <FormItem className="min-w-0">
              <FormLabel>
                <Trans>Paste JD text</Trans>
                {jdRequired ? <RequiredMark /> : null}
              </FormLabel>
              <FormControl render={<Textarea rows={10} className="h-60 min-h-60 resize-y" {...field} />} />
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid min-w-0 gap-1.5" data-testid="jd-image-column">
          <label
            htmlFor="jd-ocr-image"
            className="mb-0.5 text-sm leading-none font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            <Trans>Upload JD image (optional)</Trans>
          </label>
          <div
            data-testid="jd-image-dropzone"
            className={cn(
              "flex h-60 min-h-60 flex-col rounded-md border border-dashed bg-muted/20 p-4 transition-colors",
              isDraggingJdImage && "border-primary bg-primary/5",
            )}
            onDragOver={(event) => {
              event.preventDefault();
              setIsDraggingJdImage(true);
            }}
            onDragLeave={() => setIsDraggingJdImage(false)}
            onDrop={onJdImageDrop}
          >
            <input
              id="jd-ocr-image"
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="sr-only"
              onChange={onJdImageChange}
            />
            <label
              htmlFor="jd-ocr-image"
              className="flex flex-1 cursor-pointer flex-col items-center justify-center gap-3 text-center"
            >
              <span className="rounded-full bg-background p-3 shadow-xs">
                <ImageIcon className="size-5" />
              </span>
              <span className="text-sm font-medium">
                <Trans>Drag JD image here, or click to choose</Trans>
              </span>
              <span className="text-xs text-muted-foreground">
                {jdImageFile ? jdImageFile.name : <Trans>PNG / JPG / WebP, max 1 MB</Trans>}
              </span>
            </label>
            <p className="mt-3 border-t pt-3 text-xs leading-5 text-muted-foreground">
              <Trans>
                JD images are used only for this OCR/parse request and are not saved as reusable files. OCR/parse may be
                processed by overseas or third-party AI infrastructure.
              </Trans>
            </p>
          </div>
        </div>
        <Button
          type="button"
          className="h-auto min-h-9 w-full min-w-0 shrink whitespace-normal lg:col-start-2"
          data-testid="jd-parse-action"
          disabled={isParsingJd}
          onClick={onParseJd}
        >
          {isParsingJd ? <Spinner /> : <ImageIcon />}
          <Trans>Parse JD</Trans>
          {jdParseCostIndicator}
        </Button>
      </div>

      <div className="border-t pt-5">
        <div className="grid gap-4 sm:grid-cols-3">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <Trans>Resume Name</Trans>
                  <RequiredMark />
                </FormLabel>
                <FormControl render={<Input min={1} max={64} autoFocus {...field} />} />
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="company"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <Trans>Company</Trans>
                </FormLabel>
                <FormControl render={<Input {...field} value={field.value ?? ""} />} />
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="roleTitle"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <Trans>Role Title</Trans>
                </FormLabel>
                <FormControl render={<Input {...field} value={field.value ?? ""} />} />
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="supplementalInstructions"
          render={({ field }) => (
            <FormItem className="mt-4">
              <FormLabel>
                <Trans>Supplemental Instructions</Trans>
              </FormLabel>
              <FormControl
                render={
                  <Textarea
                    rows={4}
                    placeholder={t`For example: emphasize AI product experience; keep it to one page; de-emphasize early projects.`}
                    {...field}
                    value={field.value ?? ""}
                  />
                }
              />
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </>
  );
}
